/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Component, useState, forwardRef, useImperativeHandle } from 'react'
import { CHECKINICON, OrderStatus, getStatusIcon } from '../../models/order';
import { TableList, TableListHeader } from '../../_shared/table-list';
import { Icon, Input, Checkbox, Modal, Tooltip, DatePicker, message } from 'antd';
import Row from '../../_shared/row';
import { Button } from '../../_shared/button';
import SStyles from '../../_shared/styles';
import { get, size, set, debounce, uniqBy } from 'lodash';
import { getOrders, deleteOrder, undeleteOrder, syncManifests, updateOrder, getUserOrders } from '../../_shared/services/orders.service';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { fixDate, getConfig, getLicAddress, getLicName, getManifestUrl, getUrlParams, jparse, orderHasManifest, orderIsCheckedIn, orderIsCheckedOut, orderIsRejected, rowMatchesSearch, stopProp, userHasRole } from '../../actions/utils';
import { Order } from '../order'
import { CashPickupIcon, CashReturnIcon, RejReturnIcon } from '../../_shared/CustomIcons';
import { getDataFromGuid } from '../../_shared/driver-search';
import { projectOrdersForExt } from '../dashboard.actions';
import colors from '../../_shared/colors';
import { useSelector } from 'react-redux';
import { PrintLabels } from '../../_shared/printlabels';
import { stripOrderUrl } from '../checkin';
import { getStartOfWeek } from '../drive-schedule/schedule';
import { EditRow } from '../reconciliation';
import ScreenComponent, { useScreen } from '../../_shared/hooks/useScreen';
import { DropDown } from '../../_shared/dropdown';

const dateFormat = 'L';

// Card #274 round 2 — Danielle: "Same-day orders in Stage are now showing two drivers
// and/or vehicles on the sameday order. The expected behavior is to show only one
// assigned driver and one vehicle."
//
// The Driver and Vehicle columns below print one <div> per LEG of the order — the pickup
// leg in bold, the delivery leg plain. That is the documented behaviour for a layover,
// where one driver hauls to the warehouse and another runs the delivery. It is meaningless
// for a sameDay, where the two legs are the same leg BY CONSTRUCTION:
// `server/src/controllers/orders/utils.js:813-819` does `pick = dest` for
// ['dropoff','sameDay'], and the cleanup at `server/src/controllers/orders/utils.js:881`
// unsets the now-redundant pickup leg for 'dropoff' ONLY — 'sameDay' was never added to
// that list. So a same-day order is stored with `driver.pickup` byte-identical to
// `driver.delivery`, and `[pickup, del]` printed the same person and the same van twice.
//
// This is NOT a stage regression. `pick = dest` is 667137c (2025-09-16) and is in master,
// and the deployed prod and stage bundles render this cell from byte-identical code. Prod
// only looks clean because dispatchers reassign the delivery leg there by hand.
//
// Fixed in the renderer, not in the writer: adding 'sameDay' to that unset would only
// affect orders created after it deploys — every historical same-day order keeps both keys
// — and `data.order.driver.pickup` is still read by the driver-role Schedule filter
// (`../schedule/schedule.component.jsx:570-581`), the WORKHORSE hook
// (`server/src/controllers/orders/hooks.js:51-59`) and the pickupChange reassignment path
// (`server/src/controllers/orders/index.js:4045,4062-4068`). Display only — nothing here
// writes.

// A sameDay/dropoff is one job with one driver, so it shows the leg the order is actually
// filed under: the DELIVERY leg. Same rule round 1 gave the Schedule screen
// (`../schedule/schedule.component.jsx:82-83`; the server files these rows under
// `data.order.route` — `server/src/controllers/orders/index.js:369-370`), the same rule the
// order form already follows by hiding Pickup Route Driver for sameDay
// (`../order/order.component.jsx:1253-1257`), and the same rule the Chrome extension
// already uses when it fills exactly one METRC transporter row
// (`ext/src/views/Popup/orders/index.js:277-287`).
const DELIVERY_LEG_ONLY_TYPES = ['sameDay', 'dropoff'];

// The two strings the columns print, factored out so the de-dup key below is literally what
// the reader would see rather than a second opinion about it.
const legDriverText = leg => `${get(leg, 'driver.name', 'none')} | ${get(leg, 'driver.occLic', 'none')} | ${get(leg, 'driver.lic', 'none')}`;

const legVehicleText = leg => {
  const hasvin = getConfig('orders.showVin', true) && get(leg, 'vehicle.vin', '');
  return `${get(leg, 'vehicle.name', 'none')} | ${get(leg, 'vehicle.lic', 'none')}${hasvin ? ' | ' + hasvin : ''}`;
};

// De-dup key: the WHOLE leg — driver AND vehicle together — and ONE key shared by both
// columns, so they can never disagree and leave a single driver line sitting beside two
// vehicle lines. Keying each column on its own value would do exactly that to the ~323 prod
// / 328 stage layovers that run the same driver in two different vans; those legitimately
// keep two lines, in both columns.
//
// Route is deliberately NOT in the key: it is printed in neither column, so two legs that
// differ only by route are two identical lines on screen and must collapse. The raw guids
// are not in the key either — two distinct vehicle rows that print the same `name | lic` are
// indistinguishable to the reader, while two legs sharing a driver id but printing different
// plates (`Kia Sedona | ARL-V31` vs `Kia Sedona | BSF-J27`) must stay apart. Comparing the
// printed text can therefore never hide a difference the reader could have seen, and it does
// not care whether the leg came from getDataFromGuid or from a stored
// `driver._pickup` / `driver._delivery` blob written by the WORKHORSE hook.
const legIdentity = leg => `${legDriverText(leg)} :: ${legVehicleText(leg)}`;

// Resolve an order's driver/vehicle legs into the lines to print. Shared by the Driver and
// the Vehicle column; each then filters on its own field exactly as it did before, so a leg
// carrying only one of the two is unaffected by this.
const getDriverLegs = (record) => {
  const hasChange = get(record, 'data.order.driver.pickupChange');
  const shouldHighlight = hasChange && moment(hasChange).diff(moment(), 'hours') <= 24;

  const pickup = getConfig('orders.showPickupDriver', true) ? get(record, 'data.order.driver._pickup', { ...getDataFromGuid(get(record, 'data.order.driver.pickup')), style: { fontWeight: 'bold', color: shouldHighlight ? colors.error : undefined } }) : null;
  // `let`, not `const`: the sameDay/dropoff branch below merges the pickup leg's fields into
  // it before returning. That happens AFTER `legs` is built, so `legs` — and therefore the
  // de-dup path — still sees the untouched delivery leg.
  let del = get(record, 'data.order.driver._delivery', { ...getDataFromGuid(get(record, 'data.order.driver.delivery')) });

  // `getDataFromGuid(undefined)` still hands back an object, just an empty one, so a missing
  // leg has to be recognised by what it resolved to. Without this an order with only one leg
  // could print a blank second line or the string `none | none | none`.
  const legs = [pickup, del].filter(leg => !!leg && (!!leg.driver || !!leg.vehicle));

  // Only ever applied to a line that two legs collapsed INTO. It is plain, never bold: the
  // column header reads "Pickup driver is listed in bold", so a lone bold line would misread
  // as "pickup leg only, no delivery driver assigned". The red pickupChange warning is kept
  // — on a same-day the reassignment sweep moves both legs together and stamps pickupChange
  // (`server/src/controllers/orders/index.js:4062-4068`), so it genuinely means "this order's
  // one assignment changed recently".
  const collapsed = leg => ({ ...leg, style: { ...get(leg, 'style'), fontWeight: undefined, color: shouldHighlight ? colors.error : undefined } });

  // Nothing to de-duplicate — including a sameDay that only ever got one leg written. Left
  // exactly as it renders today, bold pickup and all.
  if (legs.length < 2) { return legs; }

  // A sameDay/dropoff shows the DELIVERY leg — but per FIELD, never the leg wholesale. A leg
  // can resolve one half of the pair and not the other: a hard-deleted driver row leaves
  // `vehicle` resolving and `driver` undefined, and the reverse happens too. Returning the
  // delivery leg whole therefore EMPTIED a column that the old two-line bundle had filled —
  // over all 77,133 non-deleted dev orders, 49 rows lost the Driver column and 7 lost the
  // Vehicle column (e.g. 404R-00411-141135's driver, 404R-00031-1301's van). The `||` fires
  // only where the delivery leg has no answer of its own, so the delivery leg still wins
  // wherever it resolves and a stale pickup driver can never displace a real delivery one.
  // Still exactly one line — the whole point of the card — and a field neither leg resolves
  // stays undefined, which each column's own `d.driver` / `d.vehicle` filter drops rather
  // than printing blank. `del` is re-pointed at a NEW object; the record is never mutated.
  if (DELIVERY_LEG_ONLY_TYPES.includes(get(record, 'data.order.orderType'))) {
    del = { ...del, driver: del.driver || get(pickup, 'driver'), vehicle: del.vehicle || get(pickup, 'vehicle') };
    return [collapsed(del)];
  }

  const deduped = uniqBy(legs, legIdentity);

  return deduped.length === legs.length ? legs : deduped.map(collapsed);
};

const COLS = (opts) => {
  // Card #256 round 7 — master switch for the whole payment-requirement
  // feature (Edit Settings > `orders.paymentRequirementEnabled`, default OFF).
  // Read ONCE per column build, not per row. `colors.orderRequiresPayment` is
  // deliberately NOT self-gating — see the comment above it in
  // ../../_shared/colors.js — so both Order ID columns below AND this flag in
  // front of their call to it.
  const paymentRequirementEnabled = !!getConfig('orders.paymentRequirementEnabled', false);

  if (!opts.isAdmin) {
    return [
      {
        title: 'Order ID',
        dataIndex: 'orderId',
        width: 100,
        fixed: opts.screen.isMobile ? 'left' : undefined,
        sortDirections: ['descend', 'ascend'],
        sorter: (_a, _b) => {
          try {
            const a = get(_a, 'orderId');
            const b = get(_b, 'orderId');
            return a.localeCompare(b, 'en', { numeric: true })
          } catch (err) {
            return 0;
          }
        },
        render: (text, row) => {
          const orderType = get(row, 'data.order.orderType');
          const isRejReturnOrder = orderType === 'rej_return';
          const isCashReturn = get(row, 'data.order.cashReturn') && !isRejReturnOrder;
          const isCashPickup = orderType === 'cash_pickup';
          const checkedout = orderIsCheckedOut(row);
          const isRejected = orderIsRejected(row);
          // Card #256 — orange Order ID + tag when payment is required-and-unrecorded,
          // and only while the master switch is on (round 7).
          const requiresPayment = paymentRequirementEnabled && colors.orderRequiresPayment(row);
          const { isSigned, hasFile, file } = orderHasManifest(row);
          const showSigned = isSigned && [OrderStatus.fullrejection, OrderStatus.partialrejection, OrderStatus.delivered].includes(get(row, 'data.order.status'));

          const manClick = (e) => {
            stopProp(e);
            window.open(getManifestUrl(file), '_blank').focus();
          }

          return (
            <div style={isRejected ? { color: colors.error, fontWeight: 'bold' } : requiresPayment ? { color: colors.warning, fontWeight: 'bold' } : checkedout ? { color: colors.highlight, fontWeight: 'bold' } : {}}>
              <span>{text}</span>
              {requiresPayment && <span title="Payment required before this order can be completed" style={{ marginLeft: 6, padding: '0 6px', border: `1px solid ${colors.warning}`, color: colors.warning, borderRadius: 3, fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap' }}>Payment Required</span>}
              {isRejReturnOrder && <RejReturnIcon />}
              {isCashReturn && <CashReturnIcon />}
              {isCashPickup && <CashPickupIcon />}
              {checkedout && !opts.isIncoming && <Icon type={CHECKINICON} title="Order is Checked Out" style={{ marginLeft: 2 }} />}
              {!showSigned && hasFile && <Icon type="file-protect" title="Order has manifest uploaded" style={{ marginLeft: 2 }} />}
              {showSigned && <div><a title="Click to see completed manifest" onClick={manClick}>Manifest <Icon type="file-protect" style={{ marginLeft: 2 }} /></a></div>}
            </div>
          )
        }
      },
      !opts.isIncoming && {
        title: 'Approved Pickup Date',
        dataIndex: 'data.order.pickupDate',
        width: '7%',
        render: (text, row) => {
          return (
            <span>{text ? moment(text).format(dateFormat) : 'N/A'}</span>
          )
        },
        sortDirections: ['descend', 'ascend'],
        sorter: (_a, _b) => {
          const a = get(_a, 'data.order.deliveryDate');
          const b = get(_b, 'data.order.deliveryDate');
          return new Date(b) - new Date(a);
        },
      },
      {
        title: () => <span title="GPS will attempt to meet all approved delivery dates. Approved delivery dates do not guarantee delivery on that date.">Approved Delivery Date</span>,
        dataIndex: 'data.order.deliveryDate',
        width: opts.isIncoming ? '10%' : '7%',
        render: (text, row) => {
          // const route = jparse(get(row, 'data.order.route', '{}'));
          const isOvernight = false;// !opts.isAdmin && get(route, 'overnight.0') === 'true';
          return (
            <span title="GPS will attempt to meet all approved delivery dates. Approved delivery dates do not guarantee delivery on that date.">{text ? moment(text).add(isOvernight ? 1 : 0, 'days').format(dateFormat) : 'N/A'}{isOvernight && <Icon title="Overnight" style={{ marginLeft: 5 }} type="clock-circle" />}</span>
          )
        },
        sortDirections: ['descend', 'ascend'],
        sorter: (_a, _b) => {
          const a = get(_a, 'data.order.deliveryDate');
          const b = get(_b, 'data.order.deliveryDate');
          return new Date(b) - new Date(a);
        },
      },
      opts.isIncoming && {
        title: 'Pickup. Name',
        dataIndex: 'data.pickupCompany.Licensee',
        width: '14%',
        render: (text, record) => {
          const ln = get(record, 'data.pickupCompany.License #');
          const txt = getLicName(ln);
          return (
            <span>{txt}</span>
          )
        },
        sortDirections: ['descend', 'ascend'],
        sorter: (_a, _b) => {
          try {
            const a = get(_a, 'data.pickupCompany.Licensee');
            const b = get(_b, 'data.pickupCompany.Licensee');
            return a.localeCompare(b, 'en', { numeric: true })
          } catch (err) {
            return 0;
          }
        },
      },
      {
        title: 'Pickup Destination',
        dataIndex: 'data.pickupCompany[Street Address]',
        width: opts.isIncoming ? '16%' : '15%',
        sortDirections: ['descend', 'ascend'],
        sorter: (_a, _b) => {
          try {
            const a = get(_a, 'data.pickupCompany[Street Address]');
            const b = get(_b, 'data.pickupCompany[Street Address]');
            return a.localeCompare(b, 'en', { numeric: true })
          } catch (err) {
            return 0;
          }
        },
        render: (text, record) => {
          const key = get(record, 'data.order.pickupLN');
          const addr = getLicAddress(key);
  
          return (
            <span>{addr}</span>
          )
        },
      },
      {
        title: 'Dest. Name',
        dataIndex: 'data.destCompany.Licensee',
        width: opts.isIncoming ? '14%' : '12%',
        render: (text, record) => {
          const ln = get(record, 'data.destCompany.License #');
          const txt = getLicName(ln);
          return (
            <span>{txt}</span>
          )
        },
        sortDirections: ['descend', 'ascend'],
        sorter: (_a, _b) => {
          try {
            const a = get(_a, 'data.destCompany.Licensee');
            const b = get(_b, 'data.destCompany.Licensee');
            return a.localeCompare(b, 'en', { numeric: true })
          } catch (err) {
            return 0;
          }
        },
      },
      {
        title: 'Destination',
        dataIndex: 'data.destCompany[Street Address]',
        width: opts.isIncoming ? '16%' :  '14%',
        sortDirections: ['descend', 'ascend'],
        sorter: (_a, _b) => {
          try {
            const a = get(_a, 'data.destCompany[Street Address]');
            const b = get(_b, 'data.destCompany[Street Address]');
            return a.localeCompare(b, 'en', { numeric: true })
          } catch (err) {
            return 0;
          }
        },
        render: (text, record) => {
          const zip = get(record, 'data.destCompany.Zip');
          const city = get(record, 'data.destCompany.City');
          const txt = city && zip ? `${text}, ${city}, ${zip}` : text;
          const key = get(record, 'data.order.destinationLN');
          const address = getLicAddress(key);
          return (
            <span>{address || txt}</span>
          )
        },
      },
      opts.isIncoming && {
        title: 'Manifest No.',
        dataIndex: 'data.billing.manifest',
        width: '10%',
      },
      !opts.isPublic && !opts.isIncoming && {
        title: () => {
          return (
            <div>
              <span>Driver</span>
              <Tooltip placement="top" title={`Pickup driver is listed in bold. Any changes to pickup driver will be highlighted in red.`}>
                <Icon type="info-circle" style={{ marginLeft: 10, color: colors.highlight }} />
              </Tooltip>
            </div>
          )
        },
        dataIndex: 'data.order.driver',
        width: '18%',
        render: (text, record) => {
          // Card #274 round 2 — the leg list comes from getDriverLegs (see the block above
          // the COLS definition): a sameDay/dropoff prints its delivery leg, and any two legs
          // that would print the same driver AND the same vehicle collapse to one plain line.
          // The `d.driver` filter is unchanged, so a leg with a vehicle but no driver still
          // contributes nothing here.
          return getDriverLegs(record).filter(d => !!d && d.driver).map((d, i) => <div key={i} style={d.style}>{legDriverText(d)}</div>)
        },
      },
      !opts.isPublic && !opts.isIncoming &&  {
        title: () => {
          return (
            <div>
              <span>Vehicle</span>
              <Tooltip placement="top" title={`Pickup vehicle is listed in bold.`}>
                <Icon type="info-circle" style={{ marginLeft: 10, color: colors.highlight }} />
              </Tooltip>
            </div>
          )
        },
        dataIndex: 'data.order.driver.pickup',
        width: '12%',
        render: (text, record) => {
          // Card #274 round 2 — the SAME leg list the Driver column above renders, which is
          // what keeps the two columns from disagreeing about how many lines an order has.
          // `orders.showVin` still gates the VIN suffix, inside legVehicleText.
          return getDriverLegs(record).filter(d => !!d && d.vehicle).map((d, i) => <div key={i} style={d.style}>{legVehicleText(d)}</div>)
        },
      },
      !opts.isPublic && {
        title: 'Status',
        dataIndex: 'data.order.status',
        width: '3%',
        render: (text, record) => {
          const ico = getStatusIcon(record, { isAdmin: opts.isAdmin });
  
          return (
            <Icon
              type={ico.icon}
              title={ico.title}
              css={css(`color: ${ico.color};`)}
            />
          );
        },
      },
      {
        title: 'Box Count',
        dataIndex: 'data.order.weight',
        width: '6%',
        render: (t, row) => {
          const _opts = {
            ...opts,
            disabled: !!opts.isIncoming
          }
          return EditRow(t, row, 'data.order.weight', _opts)
        }
      }
    ].filter(f => !!f).map(m => ({ ...m, width: opts.isPublic ? `${100 / 7}%` : m.width }));
  }
  return [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      width: 100,
      fixed: opts.screen.isMobile ? 'left' : undefined,
      sortDirections: ['descend', 'ascend'],
      sorter: (_a, _b) => {
        try {
          const a = get(_a, 'orderId');
          const b = get(_b, 'orderId');
          return a.localeCompare(b, 'en', { numeric: true })
        } catch (err) {
          return 0;
        }
      },
      render: (text, row) => {
        const orderType = get(row, 'data.order.orderType');
        const isRejReturnOrder = orderType === 'rej_return';
        const isCashReturn = get(row, 'data.order.cashReturn') && !isRejReturnOrder;
        const isCashPickup = orderType === 'cash_pickup';
        const checkedin = orderIsCheckedIn(row);
        const isRejected = orderIsRejected(row);
        // Card #256 — orange Order ID + tag when payment is required-and-unrecorded,
        // and only while the master switch is on (round 7).
        const requiresPayment = paymentRequirementEnabled && colors.orderRequiresPayment(row);
        const { hasFile } = orderHasManifest(row);

        return (
          <div style={isRejected ? { color: colors.error, fontWeight: 'bold' } : requiresPayment ? { color: colors.warning, fontWeight: 'bold' } : checkedin ? { color: colors.highlight, fontWeight: 'bold' } : {}}>
            <span>{text}</span>
            {requiresPayment && <span title="Payment required before this order can be completed" style={{ marginLeft: 6, padding: '0 6px', border: `1px solid ${colors.warning}`, color: colors.warning, borderRadius: 3, fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap' }}>Payment Required</span>}
            {isRejReturnOrder && <RejReturnIcon />}
            {isCashReturn && <CashReturnIcon />}
            {isCashPickup && <CashPickupIcon />}
            {checkedin && <Icon type={CHECKINICON} style={{ marginLeft: 2 }} />}
            {hasFile && <Icon type="file-protect" title="Order has manifest" style={{ marginLeft: 2 }} />}
          </div>
        )
      }
    },
    {
      title: 'Date Placed',
      dataIndex: 'data.dateSubmitted',
      width: '10%',
      render: (text) => {
        return (
          <span>{moment(text).format(dateFormat)}</span>
        )
      },
      sortDirections: ['descend', 'ascend'],
      sorter: (_a, _b) => {
        const a = get(_a, 'data.dateSubmitted');
        const b = get(_b, 'data.dateSubmitted');
        return new Date(b) - new Date(a);
      },
    },
    {
      title: 'Approved Pickup Date',
      dataIndex: 'data.order.pickupDate',
      width: '8%',
      render: (text, row) => {
        return (
          <span>{text ? moment(text).format(dateFormat) : 'N/A'}</span>
        )
      },
      sortDirections: ['descend', 'ascend'],
      sorter: (_a, _b) => {
        const a = get(_a, 'data.order.deliveryDate');
        const b = get(_b, 'data.order.deliveryDate');
        return new Date(b) - new Date(a);
      },
    },
    {
      title: 'Approved Delivery Date',
      dataIndex: 'data.order.deliveryDate',
      width: '8%',
      render: (text, row) => {
        const route = jparse(get(row, 'data.order.route', '{}'));
        const isOvernight = !opts.isAdmin && get(route, 'overnight.0') === 'true';
        return (
          <span>{text ? moment(text).add(isOvernight ? 1 : 0, 'days').format(dateFormat) : 'N/A'}{isOvernight && <Icon title="Overnight" style={{ marginLeft: 5 }} type="clock-circle" />}</span>
        )
      },
      sortDirections: ['descend', 'ascend'],
      sorter: (_a, _b) => {
        const a = get(_a, 'data.order.deliveryDate');
        const b = get(_b, 'data.order.deliveryDate');
        return new Date(b) - new Date(a);
      },
    },
    {
      title: 'Vendor',
      dataIndex: 'data.pickupCompany.Licensee',
      width: '15%',
      sortDirections: ['descend', 'ascend'],
      sorter: (_a, _b) => {
        try {
          const a = get(_a, 'data.pickupCompany.Licensee');
          const b = get(_b, 'data.pickupCompany.Licensee');
          return a.localeCompare(b, 'en', { numeric: true })
        } catch (err) {
          return 0;
        }
      },
    },
    {
      title: 'Pickup',
      dataIndex: 'data.pickupCompany[Street Address]',
      width: '16%',
      sortDirections: ['descend', 'ascend'],
      sorter: (_a, _b) => {
        try {
          const a = get(_a, 'data.pickupCompany[Street Address]');
          const b = get(_b, 'data.pickupCompany[Street Address]');
          return a.localeCompare(b, 'en', { numeric: true })
        } catch (err) {
          return 0;
        }
      },
      render: (text, record) => {
        const key = get(record, 'data.order.pickupLN');
        const addr = getLicAddress(key);

        return (
          <span>{addr || text}</span>
        )
      },
    },
    {
      title: 'Dest. Name',
      dataIndex: 'data.destCompany.Licensee',
      width: '15%',
      render: (text, record) => {
        const ln = get(record, 'data.destCompany.License #');
        const txt = getLicName(ln);
        return (
          <span>{txt}</span>
        )
      },
      sortDirections: ['descend', 'ascend'],
      sorter: (_a, _b) => {
        try {
          const a = get(_a, 'data.destCompany.Licensee');
          const b = get(_b, 'data.destCompany.Licensee');
          return a.localeCompare(b, 'en', { numeric: true })
        } catch (err) {
          return 0;
        }
      },
    },
    {
      title: 'Destination',
      dataIndex: 'data.destCompany[Street Address]',
      width: '16%',
      sortDirections: ['descend', 'ascend'],
      sorter: (_a, _b) => {
        try {
          const a = get(_a, 'data.destCompany[Street Address]');
          const b = get(_b, 'data.destCompany[Street Address]');
          return a.localeCompare(b, 'en', { numeric: true })
        } catch (err) {
          return 0;
        }
      },
      render: (text, record) => {
        const key = get(record, 'data.order.destinationLN');
        const address = getLicAddress(key);
        return (
          <span>{address || text}</span>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'data.order.status',
      width: '2%',
      render: (text, record) => {
        const ico = getStatusIcon(record);
  
        return (
          <Icon
            type={ico.icon}
            title={ico.title}
            css={css(`color: ${ico.color};`)}
          />
        );
      },
    },
  ].filter(f => !!f);
}

export const sortOrders = (a, b) => {
  const datea = new Date(get(a, 'data.dateSubmitted', get(a, 'created')));
  const dateb = new Date(get(b, 'data.dateSubmitted', get(b, 'created')));

  const appA = get(a, 'data.order.deliveryDate');
  const appB = get(b, 'data.order.deliveryDate');

  if (appA && !appB) {
    return 1;
  }
  if (!appA && appB) {
    return -1;
  }

  return dateb - datea;
}

export const orderSearch = (orders = [],  searchTerm = '', opts = {}) => {
  const st = searchTerm.toLowerCase();
  const odef = get(opts, 'orderDef.value');
  
  return orders.filter(a => {
    const tosearch = [
      `${get(a, 'data.pickupCompany[Street Address]', '').toLowerCase()} ${get(a, 'data.destCompany[Street Address]', '').toLowerCase()}`, //address 
      get(a, 'orderId', '').toLowerCase(), //id
      get(a, 'id', '').toLowerCase(), //guid
      (get(a, 'data.order.destinationLN', '') || '').toLowerCase(), //destid
      `${get(a, 'data.pickupCompany.Licensee', '').toLowerCase()} ${get(a, 'data.destCompany.Licensee', '').toLowerCase()}`, //name
      get(a, 'data.order.orderType', '').toLowerCase(), //type
      `${get(a, 'data.billing.invoice', '')}`.toLowerCase(), //invoiceid,
      `${get(a, 'data.billing.manifest', '')}`.toLowerCase(), //manifestid
    ]

    // Card #277 — the eight fields above are the original list and stay, because two of
    // them (the row guid and destinationLN) are not columns at all and pasting an order
    // URL has always had to work. `cols` adds everything the caller's grid is actually
    // showing on top: resolved licence names and addresses, the driver and vehicle legs,
    // the status label behind its icon, and the dates in their displayed format.
    const cols = get(opts, 'cols');
    const found = !!tosearch.find(s => s.indexOf(st) > -1)
      || (!!cols && rowMatchesSearch(cols, a, st, { useRender: true }));

    let foundodef = true;
    if (odef) {
      foundodef = get(a, 'data.order.orderDef') == odef;
    }

    return found && foundodef;
  })
}

export class OrdersComponent extends Component {
  constructor(props) {
    super(props);

    const { userid } = getUrlParams(window.location.href);

    // Card #151: automated emails deep-link to /dashboard/orders?search=<orderId>.
    // Seed the existing search state from the query param so the list arrives
    // pre-filtered (filterOrders runs after getOrders in componentDidMount).
    const { search: urlSearchTerm = '' } = getUrlParams(get(props, 'location.search') || window.location.href);

    const orderTimes = this.getDateConfigs();

    const between = [moment().subtract(orderTimes.placedTimeStart, 'days'), moment().add(orderTimes.placedTimeEnd, 'days')];

    this.state = {
      allOrders: [],
      selectedOrders: [],
      deletedOrders: [],
      orders: [],
      loading: false,
      loadingManifests: false,
      error: undefined,
      success: undefined,
      editModal: undefined,
      searchTerm: urlSearchTerm,
      isAdmin: false, //whether to setup admin funcitonalitiy
      isPrintingLabels: false,
      searchVal: urlSearchTerm,
      isPublic: !!userid ? true : false,
      userid: userid,
      between,
      saving: false,
      orderDef: undefined,
    }
  }
  getDateConfigs = () => {
    return {
      placedTimeStart: 14,
      placedTimeEnd: 14,
      ...getConfig('orders.dates', {}),
    }
  }
  componentDidMount = async () => {
    await this.getOrders();
    const { id } = getUrlParams(window.location.href);
    if (id) {
      const order = this.state.orders.find(o => o.id === id) || { id };
      this.selectOrder(order);
    }
  }
  isAdmin = () => {
    const { sState } = this.props;
    const { location } = this.props.history;
    let isAdmin = false;
    if (location.pathname.indexOf('admin') > -1 && userHasRole([0, 1], sState)) {
      isAdmin = true;
    }
    return isAdmin;
  }
  isIncoming = () => {
    const { location } = this.props.history;
    let is = false;
    if (location.pathname.indexOf('incomingorders') > -1) {
      is = true;
    }
    return is;
  }
  isPlaced = () => {
    const { location } = this.props.history;
    let is = false;
    if (location.pathname.indexOf('placedorders') > -1) {
      is = true;
    }
    return is;
  }
  getOrders = async () => {
    try {
      const { isPublic, userid } = this.state;
      let isAdmin = this.isAdmin();   
      const isIncoming = this.isIncoming(); 
      // const isPlaced = this.isPlaced();  

      this.TABLE && this.TABLE.clearSelected();
      this.setState({ loading: true });
      const userId = isPublic ? userid : get(this.props.sState, 'user.id', '__none__');
      let orders;

      const [bStart, bEnd] = this.state.between || [];
      const rangeBetween = bStart && bEnd
        ? [moment(bStart).startOf('day'), moment(bEnd).endOf('day')]
        : this.state.between;

      orders = (await getOrders({
        userId: isAdmin ? undefined : userId,
        isIncoming,
        between: rangeBetween,
      })).sort(sortOrders);

      // if (isPublic) {
      //   orders = (await getOrders({ 
      //     userId, 
      //     between: [this.state.week, moment(this.state.week).add(1, 'week')] 
      //   })).sort(sortOrders);
      // } else if (isPlaced) {
      //   orders = (await getUserOrders({ 
      //     userId, 
      //     between: [this.state.week, moment(this.state.week).add(orderTimes.placedTime, 'days')] 
      //   })).sort(sortOrders);
      // } else {
      //   orders = (await getOrders(isAdmin ? null : { userId, isIncoming }, !allHistory ? { greaterThen: moment().subtract(orderTimes.adminGreaterThan, 'days').toISOString() } : {})).sort(sortOrders);
      // }

      if (!isAdmin && !isPublic && !isIncoming) {
        this.pushOrdersToExtension(orders, rangeBetween);
      }

      this.setState({ 
        allOrders: orders, 
        orders, 
        loading: false, 
        isAdmin,
      }, this.filterOrders);
    } catch (err) {
      this.setState({ loading: false, error: err.message });
    }
  }
  // Card #101 — this screen's push is date-ranged, so it can hand the extension
  // far fewer orders than the account-wide pull it now takes precedence over.
  // Send the range along so the extension names what it is holding instead of
  // showing a bare count with nothing explaining why it narrowed.
  pushOrdersToExtension = (orders, between) => {
    try {
      if (!this.props.setOrders) { return; }
      const [start, end] = between || [];
      const scope = start && end
        ? `${moment(start).format('L')}–${moment(end).format('L')}`
        : '';
      this.props.setOrders(projectOrdersForExt(orders), scope);
    } catch (err) {
      console.warn(err);
    }
  }
  filterOrders = () => {
    const { allOrders, searchTerm, orderDef } = this.state;
    // Card #277 — hand the search the same columns the grid below is built from, so it
    // matches whatever this variant of the screen is actually showing (the admin, public
    // and incoming views each carry a different set).
    const cols = COLS({ ...this.state, screen: this.props.screen, isIncoming: this.isIncoming() });
    const orders = orderSearch(allOrders, searchTerm, { orderDef, cols });
    this.setState({ orders });
  }
  selectOrder = (record, index) => {
    if (this.state.isPublic || this.isIncoming()) { return };
    if (record) {
      console.log('record', record);
      this.setState({ editModal: { state: { editOrder: record } } });
    }
  }
  onSearch = (st) => {
    this.setState({ searchTerm: st }, this.filterOrders);
  }
  filterDelivered = (deliveredOnly) => {
    const { allOrders } = this.state;
    const orders = deliveredOnly ? allOrders.filter(o => get(o, 'data.order.status', 0) >= OrderStatus.delivered) : allOrders;
    this.setState({ orders });
  }
  unDeleteItems = async () => {
    const { deletedOrders } = this.state;

    try {
      this.setState({ loading: true });
      await Promise.all(deletedOrders.map(async so => await undeleteOrder(so)))
      this.setState({ deletedOrders: [], selectedOrders: [] }, this.getOrders);
    } catch (err) {
      this.setState({ error: err.message });
    } finally {
      this.setState({ loading: false });
    }
  }
  syncManifests = async () => {
    const go = async () => {
      try {
        this.setState({ loadingManifests: true });
        await syncManifests();
      } catch (err) {
        this.setState({ error: err.message });
      } finally {
        this.setState({ loadingManifests: false });
      }
    }

    Modal.confirm({
      title: 'Sync Manifests',
      content: 'This request will take up to 5 minutes. Please wait, or return in ~5 mins.',
      onOk: go,
      onCancel: () => 0,
    })
  }
  deleteSelectedItems = async () => {
    const { selectedOrders } = this.state;

    try {
      this.setState({ loading: true });
      await Promise.all(selectedOrders.map(async so => await deleteOrder(so)))
      this.setState({ deletedOrders: selectedOrders, selectedOrders: [] }, this.getOrders);
    } catch (err) {
      this.setState({ error: err.message });
    } finally {
      this.setState({ loading: false });
    }
  }
  onDelete = () => {
    const { selectedOrders } = this.state;
    Modal.confirm({
      title: `Delete ${size(selectedOrders)} Orders?`,
      content: `Are you sure you want to delete these orders?`,
      onOk: this.deleteSelectedItems,
      onCancel: () => 0,
    })
  }
  unDelete = () => {
    const { deletedOrders } = this.state;
    Modal.confirm({
      title: `Delete ${size(deletedOrders)} Orders?`,
      content: `Are you sure you want to delete these orders?`,
      onOk: this.unDeleteItems,
      onCancel: () => 0,
    })
  }
  onSelectedRowsChange = (selectedKeys, selectedOrders) => {
    this.setState({ selectedOrders });
  }
  onCheckChange = (e) => {
    const checked = e.target.checked;
    this.filterDelivered(checked);
  }
  closeEditModal = (shouldUpdate) => {
    this.setState({ editModal: undefined });
    if (shouldUpdate) {
      this.getOrders();
    }
  }
  selectWeek = (a) => {
    const week = getStartOfWeek(a);
    this.setState({ week }, this.getOrders);
  }
  changeBetween = e => {
    const [start, end] = e;

    if (!start || !end) {
      return alert(`Date range must be selected! Please try again.`);
    }

    const dateConfigs = this.getDateConfigs();

    const diff = end.diff(start, 'days');

    if (diff > dateConfigs.placedTime) {
      return alert(`Date range selected cannot be greater than ${dateConfigs.placedTime} days. Please try again.`);
    }

    this.setState({ between: e }, this.getOrders)
  }
  saveOrder = debounce(async (order) => {
    try {
      this.setState({ saving: true });
      await updateOrder(order);
    } catch (err) {
      message.error(`${err.message}`);
    } finally {
      this.setState({ saving: false });
    }
  }, 800, { leading: false });
  onChangeText = (val, row, key) => {
    const orderIndex = this.state.orders.findIndex(o => o.id === row.id);
    set(this.state, `orders.${orderIndex}.${key}`, val);
    this.setState({ orders: [...this.state.orders] });
    
    if (!this.state.isPublic) {
      this.saveOrder(get(this.state, `orders.${orderIndex}`));
    }
  }
  onChangeDef = v => {
    const { title, value } = get(v, 'item.props', {});
    this.setState({ orderDef: { title, value } }, this.filterOrders);
  }
  render() {
    const { screen } = this.props;
    const {
      loading,
      loadingManifests,
      orders,
      selectedOrders,
      deletedOrders,
      isAdmin,
      editModal,
      isPublic,
    } = this.state;

    const isIncoming = this.isIncoming();

    // Card #278 — the admin "Sync Manifests" button is hideable per client from Edit
    // Settings ("Show Sync Manifests Button" / orders.showSyncManifests, Orders category).
    // Only an explicit false hides it, so every client that never set the key — and any
    // page loaded before the config finished fetching — keeps today's behavior.
    const showSyncManifests = getConfig('orders.showSyncManifests', true) !== false;

    return (
      <div className='no-print'>
        <Row>
          <DatePicker.RangePicker style={{ width: 500 }} value={this.state.between} placeholder="Select Dates" onChange={this.changeBetween} />
        </Row>
        <TableListHeader>
          <Row>
            <Row css={css(screen.isMobile ? `width: 100%` : `width: 600px;`)}>
              <Input.Search
                placeholder={'Search by Order ID, Manifest No., Name or Address...'}
                enterButton
                onSearch={this.onSearch}
                value={this.state.searchVal}
                onChange={e => this.setState({ searchVal: stripOrderUrl(e.target.value) })}
              />
              {!isPublic && <Checkbox onChange={this.onCheckChange} css={css(SStyles.row, `margin-left: 10px; width: 250px;`)}>
                Show Only Delivered
              </Checkbox>}
              {!!getConfig('orders.isInternalDist') && <DropDown text={`Order Def: ${get(this, 'state.orderDef.title', 'All')}`} onChange={this.onChangeDef} options={[{ title: 'Internal', value: 'int' }, { title: 'External', value: 'ext' }, { title: 'All', value: undefined }]} />}
            </Row>
            {!isPublic && !screen.isMobile && !isIncoming && <div css={css('width: auto; flex: 1 1 auto; font-weight: bold; text-align: center; padding: 0 20px;')}>
              <div dangerouslySetInnerHTML={{ __html: getConfig('orders.infoText', '') }} />
            </div>}
            <Row css={css(`
              width: auto;
              justify-content: flex-end;
              align-items: flex-start;
              & > * + * { margin-left: 10px; }
            `)}>
              <PrintLabels orders={selectedOrders} />
              {isAdmin && !!size(selectedOrders) && <Button title={'Delete'} type="danger" onClick={this.onDelete} />}
              {isAdmin && !!size(deletedOrders) && <Button title={'Un Delete'} type="outline" onClick={this.unDelete} />}
              {isAdmin && <AdminOrderButton onClose={this.getOrders} />}
              {isAdmin && showSyncManifests && <Button title={'Sync Manifests'} loading={loadingManifests} type="outline" onClick={this.syncManifests} />}
            </Row>
          </Row>
        </TableListHeader>
        <TableList
          ref={comp => this.TABLE = comp}
          loading={loading}
          data={orders}
          columns={COLS({ ...this.state, screen, onChangeText: this.onChangeText, isIncoming: this.isIncoming() })}
          onSelectedRowsChange={this.onSelectedRowsChange}
          rowClick={isIncoming ? undefined : this.selectOrder}
          onRefresh={this.getOrders}
          scroll={screen.isMobile ? { x: 1050 } : undefined}
        />
        <OrderModal editModal={editModal} onClose={this.closeEditModal} />
      </div>
    )
  }
}

export const OrderModal = props => {
  const { editModal, onClose } = props;
  const { isMobile } = useScreen();

  if (!editModal) { return null }
  
  return (
    <Modal
      title={(
        <div>
          <div>{`Edit Order ${get(editModal, 'state.editOrder.orderId', '')}`}</div>
          {get(editModal, 'state.editOrder.created') && <div>{`Date Placed: ${moment(fixDate(get(editModal, 'state.editOrder.created'))).format('L')}`}</div>}
        </div>
      )}
      visible={!!editModal}
      width={isMobile ? '100%' : '90%'}
      style={isMobile ? { top: 0 } : { top: 30 }}
      onOk={() => onClose()}
      onCancel={() => onClose()}
      footer={(null)}
      wrapClassName="OrderModal"
    >
      {!!editModal && <Order {...editModal} onClose={onClose} isModal={true} />}
    </Modal>
  )
}

export const AdminOrderButton = forwardRef((props, ref) => {
  const sState = useSelector(st => st);
  const [state, setState] = useState({
    editModal: undefined,
    newEditModal: {
      state: {
        allAvail: true,
        isNewAdminOrder: true,
        editOrder: {
          data: {
            order: {
              status: OrderStatus.orderApproved,
            }
          }
        },
      }
    }
  });

  const isAdmin = userHasRole([0, 1], sState);

  const startOpen = (ord) => {
    const newEditModal = { ...state.newEditModal };
    if (ord) {
      set(newEditModal, 'state.editOrder', ord);
    }
    setState(s => ({ 
      ...s, 
      editModal: newEditModal
    }));
  }

  // Card #285 — forward the save/cancel flag. `Order` calls onClose(true) via
  // goBack(true) only after a successful save; the modal's X (and an in-form
  // cancel) call onClose() with no argument. This used to be swallowed here, so
  // a caller could not tell "order created" from "user backed out" — which is
  // exactly what left orphaned reconciliations behind on the Billing screen.
  const onClose = (shouldUpdate) => {
    setState(s => ({ ...s, editModal: undefined }));
    props.onClose && props.onClose(shouldUpdate);
  }

  useImperativeHandle(ref, () => ({
    open: startOpen,
  }), [state]);

  if (!isAdmin && !state.editModal) { return null }

  return (
    <div>
      <Button onClick={startOpen}>Create Order</Button>
      <OrderModal editModal={state.editModal} onClose={onClose} isModal={true} />
    </div>
  )
})

export const DriverOrderButton = props => {
  const sState = useSelector(st => st);
  
  const [state, setState] = useState({
    editModal: undefined,
    newEditModal: {
      state: {
        allAvail: true,
        isDriverOrder: true,
        editOrder: {
          data: {
            order: {
              cashReturn: true,
              orderType: 'cash_pickup',
              pickupDate: moment().startOf('day').toISOString(),
              deliveryDate: moment().add(1, 'day').startOf('day').toISOString(),
              status: OrderStatus.orderApproved,
            }
          }
        },
      }
    }
  });

  const isDriver = userHasRole([2], sState);

  const startOpen = () => {
    setState(s => ({ ...s, editModal: s.newEditModal }));
  }

  const onClose = () => {
    setState(s => ({ ...s, editModal: undefined }));
    props.onClose && props.onClose();
  }

  if (!isDriver) { return null }

  return (
    <div>
      <Button onClick={startOpen}>Payment Pickup</Button>
      <OrderModal editModal={state.editModal} onClose={onClose} isModal={true} />
    </div>
  )
}

export const Orders = ScreenComponent(withRouter(OrdersComponent));
